import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Input, Button, Typography, Card, message, Alert, Progress } from "antd";
import {
  LockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RocketOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone
} from "@ant-design/icons";
import AuthService from "../services/auth.service";

const { Title, Text } = Typography;

const C = {
  blue: "#0054A6",
  blueDark: "#003B75",
  orange: "#E8860C",
  orangeLight: "#FF8C00",
  textLight: "#888",
  border: "#eaedf2",
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Validation du mot de passe en temps réel
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
  };

  const passedChecks = Object.values(passwordChecks).filter(Boolean).length;
  const passwordStrength = (passedChecks / 5) * 100;

  const getStrengthColor = () => {
    if (passwordStrength <= 40) return "#ff4d4f";
    if (passwordStrength <= 60) return "#faad14";
    if (passwordStrength <= 80) return "#1890ff";
    return "#52c41a";
  };

  const getStrengthText = () => {
    if (passwordStrength <= 40) return "Faible";
    if (passwordStrength <= 60) return "Moyen";
    if (passwordStrength <= 80) return "Bon";
    return "Excellent";
  };

  useEffect(() => {
    if (!token || !email) {
      setErrorMsg("Lien de réinitialisation invalide ou expiré. Veuillez faire une nouvelle demande.");
    }
  }, [token, email]);

  const handleResetPassword = async () => {
    setErrorMsg("");

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setErrorMsg("Veuillez remplir tous les champs.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Les mots de passe ne correspondent pas.");
      return;
    }

    if (passedChecks < 4) {
      setErrorMsg("Le mot de passe n'est pas assez fort. Respectez au moins 4 critères.");
      return;
    }

    setLoading(true);
    try {
      await AuthService.resetPassword(token, email, newPassword);
      setSuccess(true);
      message.success("Mot de passe réinitialisé avec succès !");
    } catch (error) {
      const msg = error.response?.data?.message || "Erreur lors de la réinitialisation. Le lien a peut-être expiré.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${C.blueDark} 0%, ${C.blue} 50%, ${C.blueDark} 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}>
      <Card style={{
        width: "100%",
        maxWidth: 440,
        borderRadius: 16,
        boxShadow: "0 16px 40px rgba(0,0,0,0.15)",
        border: "none",
      }}
        bodyStyle={{ padding: 36 }}
      >
        {!success ? (
          <>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%",
                background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: `0 6px 20px ${C.blue}33`,
              }}>
                <LockOutlined style={{ fontSize: 24, color: "white" }} />
              </div>
              <Title level={2} style={{ color: C.blueDark, marginBottom: 6, fontSize: 22, fontWeight: 800 }}>
                Nouveau mot de passe
              </Title>
              <Text style={{ color: C.textLight, fontSize: 13 }}>
                Choisissez un nouveau mot de passe sécurisé
              </Text>
              {email && (
                <div style={{ marginTop: 8 }}>
                  <Text style={{ color: C.blue, fontSize: 12, fontWeight: 600 }}>{email}</Text>
                </div>
              )}
            </div>

            {/* Erreur token invalide */}
            {!token || !email ? (
              <div style={{ textAlign: "center" }}>
                <Alert
                  message="Lien invalide"
                  description="Ce lien de réinitialisation est invalide ou a expiré. Veuillez refaire une demande depuis la page de connexion."
                  type="error"
                  showIcon
                  style={{ borderRadius: 10, marginBottom: 20 }}
                />
                <Button
                  type="primary" block size="large"
                  onClick={() => navigate("/login")}
                  style={{
                    background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                    border: "none", borderRadius: 10, height: 44, fontWeight: 600,
                  }}
                >
                  Retour à la connexion
                </Button>
              </div>
            ) : (
              <>
                {/* Champ nouveau mot de passe */}
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 6 }}>
                    Nouveau mot de passe
                  </Text>
                  <Input.Password
                    prefix={<LockOutlined style={{ color: C.blue, fontSize: 15, marginRight: 6 }} />}
                    placeholder="Entrez votre nouveau mot de passe"
                    size="large"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    style={{ borderRadius: 10, border: `1.5px solid ${C.border}`, padding: "10px 12px" }}
                  />
                </div>

                {/* Indicateur de force */}
                {newPassword.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Text style={{ fontSize: 11, color: C.textLight }}>Force du mot de passe</Text>
                      <Text style={{ fontSize: 11, color: getStrengthColor(), fontWeight: 600 }}>
                        {getStrengthText()}
                      </Text>
                    </div>
                    <Progress
                      percent={passwordStrength}
                      showInfo={false}
                      strokeColor={getStrengthColor()}
                      size="small"
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "4px 12px",
                      fontSize: 11,
                    }}>
                      {[
                        { key: "length", label: "8 caractères min." },
                        { key: "uppercase", label: "1 majuscule" },
                        { key: "lowercase", label: "1 minuscule" },
                        { key: "number", label: "1 chiffre" },
                        { key: "special", label: "1 caractère spécial" },
                      ].map(({ key, label }) => (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {passwordChecks[key]
                            ? <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 12 }} />
                            : <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 12 }} />
                          }
                          <span style={{ color: passwordChecks[key] ? "#52c41a" : "#999" }}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Champ confirmation */}
                <div style={{ marginBottom: 20 }}>
                  <Text strong style={{ fontSize: 12, color: "#555", display: "block", marginBottom: 6 }}>
                    Confirmer le mot de passe
                  </Text>
                  <Input.Password
                    prefix={<LockOutlined style={{ color: C.blue, fontSize: 15, marginRight: 6 }} />}
                    placeholder="Confirmez votre nouveau mot de passe"
                    size="large"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onPressEnter={handleResetPassword}
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                    style={{ borderRadius: 10, border: `1.5px solid ${C.border}`, padding: "10px 12px" }}
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <Text style={{ color: "#ff4d4f", fontSize: 11, marginTop: 4, display: "block" }}>
                      Les mots de passe ne correspondent pas
                    </Text>
                  )}
                  {confirmPassword && newPassword === confirmPassword && confirmPassword.length > 0 && (
                    <Text style={{ color: "#52c41a", fontSize: 11, marginTop: 4, display: "block" }}>
                      ✓ Les mots de passe correspondent
                    </Text>
                  )}
                </div>

                {/* Erreur */}
                {errorMsg && (
                  <Alert
                    message={errorMsg}
                    type="error" showIcon
                    style={{ borderRadius: 10, marginBottom: 16, border: "none" }}
                  />
                )}

                {/* Bouton */}
                <Button
                  type="primary" block size="large"
                  loading={loading}
                  onClick={handleResetPassword}
                  disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
                  style={{
                    background: `linear-gradient(135deg, ${C.orange}, ${C.orangeLight})`,
                    border: "none", borderRadius: 10, height: 46,
                    fontSize: 14, fontWeight: 700,
                    boxShadow: `0 4px 16px ${C.orange}33`,
                    opacity: (!newPassword || !confirmPassword || newPassword !== confirmPassword) ? 0.6 : 1,
                  }}
                >
                  {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
                </Button>
              </>
            )}
          </>
        ) : (
          /* ============ ÉCRAN DE SUCCÈS ============ */
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div style={{
              width: 70, height: 70, borderRadius: "50%",
              background: "linear-gradient(135deg, #52c41a, #389e0d)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 6px 20px rgba(82, 196, 26, 0.3)",
            }}>
              <CheckCircleOutlined style={{ fontSize: 32, color: "white" }} />
            </div>
            <Title level={3} style={{ color: "#389e0d", marginBottom: 8, fontSize: 20, fontWeight: 700 }}>
              Mot de passe réinitialisé !
            </Title>
            <Text style={{ color: C.textLight, fontSize: 13, lineHeight: 1.7, display: "block", marginBottom: 24 }}>
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </Text>
            <Button
              type="primary" block size="large"
              onClick={() => navigate("/login")}
              style={{
                background: `linear-gradient(135deg, ${C.blue}, ${C.blueDark})`,
                border: "none", borderRadius: 10, height: 46,
                fontSize: 15, fontWeight: 700,
                boxShadow: `0 4px 16px ${C.blue}33`,
              }}
            >
              Se connecter
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;